import {
  getComplementaryProductPacks,
  hasPriceForProduct,
  getProductPrice,
  getCurrencyFromPricesDto
} from '@/models/back-end/Prices';
import { isProductInStock, getStockQuantity } from '@/models/back-end/Stock';
import {
  getProductPackName,
  setProductPackStatus,
  setProductPackUsage,
  setProductPackPrice,
  setProductPackStockQuantity,
  isProductPackGenericEyeSerum,
  ProductPack
} from '@/models/core/ProductPack';
import {
  getSerumProductPacks,
  getPrescribedComplementaryProductUsage
} from '@/models/back-end/Treatment';
import {
  getProductPacksFromGenericEyeFormulasDto,
  isProductPackWithEyeSerumGenericFormulaInStock
} from '@/models/back-end/EyeSerumGenericFormulas';
import { Price } from '@/models/core/values';
import { PackItem } from '@/models/core/PackItem';

import { PRODUCT_REFERENCES } from '@/constants/treatment';

const updatePacksStockInfo = (packs, stockDto, genericFormulasDto) => {
  return packs
    .map(pack => {
      if (isProductPackGenericEyeSerum(pack)) {
        // generic formulas take stock info from genericFormulasDto
        const isGenericInStock = isProductPackWithEyeSerumGenericFormulaInStock(
          pack,
          genericFormulasDto
        );

        if (!isGenericInStock) {
          return setProductPackStatus('out-of-stock', pack);
        }
      } else if (!isProductInStock(pack, stockDto)) {
        return setProductPackStatus('out-of-stock', pack);
      }

      return pack;
    })
    .map(pack => {
      const stockQuantity = getStockQuantity(pack, stockDto);

      return setProductPackStockQuantity(stockQuantity, pack);
    });
};

const updatePacksAvailability = (packs, pricesDto) => {
  return packs.map(pack => {
    if (!hasPriceForProduct(pack, pricesDto)) {
      return setProductPackStatus('not-available', pack);
    }

    return pack;
  });
};

const updatePacksStatuses = (packs, stockDto, genericFormulasDto, pricesDto) => {
  const packsWithStockStatusUpdated = updatePacksStockInfo(packs, stockDto, genericFormulasDto);

  // 'not-available' status has priority over 'out-of-stock'
  return updatePacksAvailability(packsWithStockStatusUpdated, pricesDto);
};

const getFreeSamplePack = (stockDto, pricesDto) => {
  const stockQuantity = stockDto.freeSampleProduct;
  const currency = getCurrencyFromPricesDto(pricesDto);

  const price = Price(0, currency);
  const packItem = PackItem({
    reference: PRODUCT_REFERENCES.FREE_SAMPLE,
    price,
    stockQuantity
  });

  return ProductPack([packItem]);
};

/**
 * Get a full list of product packs derived from a set of DTOs.
 *
 * @param {Object} treatmentDto
 * @param {Object} stockDto
 * @param {Object} pricesDto
 * @param {Object} genericFormulasDto
 * @return {ProductPack[]}
 */
export const getProductPacks = (treatmentDto, stockDto, pricesDto, genericFormulasDto) => {
  const complementaryProductPacks = getComplementaryProductPacks(pricesDto).map(pack => {
    const usage = getPrescribedComplementaryProductUsage(pack, treatmentDto);

    if (usage) {
      return setProductPackUsage(usage, pack);
    }

    return pack;
  });

  const serumProductPacks = getSerumProductPacks(treatmentDto).flatMap(pack => {
    const isEyeSerum = getProductPackName(pack) === 'eyeContourSerum';
    const hasGenericEyeFormulas = genericFormulasDto.length > 0;
    const shouldReplacePackWithEyeGenerics = isEyeSerum && hasGenericEyeFormulas;
    let processedPacks = [pack];

    if (shouldReplacePackWithEyeGenerics) {
      processedPacks = getProductPacksFromGenericEyeFormulasDto(genericFormulasDto);
    }

    processedPacks = processedPacks.map(processedPack => {
      if (!hasPriceForProduct(processedPack, pricesDto)) {
        return setProductPackStatus('not-available', processedPack);
      }

      const price = getProductPrice(processedPack, pricesDto);

      return setProductPackPrice(price, processedPack);
    });

    return processedPacks;
  });

  return [
    ...updatePacksStatuses(serumProductPacks, stockDto, genericFormulasDto, pricesDto),
    ...updatePacksStatuses(complementaryProductPacks, stockDto, genericFormulasDto, pricesDto),
    getFreeSamplePack(stockDto, pricesDto)
  ];
};
